<template>
  <UIModal
      :id="modalId"
      title="Visualizar Permissões"
      size="xl"
      :no-close-on-backdrop="false"
      :no-close-on-esc="true"
      :headerSeparator="true"
      footerClass="edit-perms__footer"
      class="edit-perms"
  >
    <template #default>
      <div class="edit-perms__top">
        <div class="edit-perms__top-left">
          <img
              width="80"
              height="84"
              :src="user.avatar || avatar"
              class="avatar"
              alt="Foto do usuário"
          />
          <div class="edit-perms__top-text">
            <p class="edit-perms__main-title">{{ user.name || '' }}</p>
            <p class="edit-perms__email">{{ user.email || '' }}</p>
            <p class="edit-perms__info">
              ID: {{ user.id || '' }} {{ formattedDate ? `· Cadastrado em ${formattedDate}` : ''  }} 
            </p>
          </div>
        </div>
        <div class="edit-perms__top-right">
          <UIBadge pill font-size="16" variant="secondary">
            {{ user.role || '' }}
          </UIBadge>
        </div>
      </div>

      <UIHorizontalTable>
        <div
            v-for="permission in permissions"
            :key="permission.id"
            class="ui-horizontal-table__items edit-perms__item"
        >
          <div class="ui-horizontal-table__left edit-perms__item-left">
            <strong>{{ permission.description }}</strong>
          </div>
          <div class="ui-horizontal-table__right edit-perms__item-right">
            <UIBadge
                v-for="accessLevel in accessLevels"
                :key="accessLevel.level"
                :variant="selectedPerm[permission.action] === accessLevel.level ? 'info' : 'secondary'"
                class="edit-perms__badge"
                v-b-tooltip.top="accessLevel.tooltip"
            >
              {{ accessLevel.perm }}
            </UIBadge>
          </div>
        </div>
      </UIHorizontalTable>
    </template>

    <template #footer>
      <UIActionButton width="206" variant="cancel" @click="hide">Fechar</UIActionButton>
    </template>
  </UIModal>
</template>

<script>
import UIModal from '@components/ui-design/UIModal'
import UIBadge from '@components/ui-design/UIBadge'
import UIHorizontalTable from '@components/ui-design/UIHorizontalTable'
import UIActionButton from '@components/ui-design/UIActionButton'
import userAvatar from "@src/assets/images/users/user-12.png"
import { AccessLevel, AccessLevelDescriptions } from '@views/configurations/enums/accessLevel'

export default {
  name: 'EditPermissionsModal',
  components: { UIModal, UIBadge, UIHorizontalTable, UIActionButton },
  props: {
    modalId: { type: String, required: true },
    user: { type: Object, required: true },
    permissions: { type: Array, required: true }
  },
  data() {
    return {
      avatar: userAvatar,
      accessLevels: [
        { perm: AccessLevelDescriptions.none, tooltip: 'Acesso bloqueado à tela', level: AccessLevel.NONE },
        { perm: AccessLevelDescriptions.reader, tooltip: 'Pode visualizar informações, sem executar ações', level: AccessLevel.READER },
        { perm: AccessLevelDescriptions.editor, tooltip: 'Pode criar, editar e remover registros', level: AccessLevel.EDITOR }
      ],
    }
  },
  computed: {
    selectedPerm() {
      return this.permissions.reduce((acc, p) => {
        if (p.action) {
          acc[p.action] = p.access_level != null ? p.access_level : null
        }
        return acc
      }, {})
    },
    formattedDate() {
      const value = this.user.granted_at
      if (!value) return ''

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''

      return date.toLocaleString('pt-BR')
    },
  },
  methods: {
    hide() {
      this.$emit('hide')
      this.$bvModal.hide(this.modalId)
    }
  }
}
</script>

<style lang="scss">
@import '@src/design/ui-design/_variables.scss';

.edit-perms {
  .ui-horizontal-table__items.edit-perms__item {
    @media (max-width: 798px) {
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
      gap: 10px;
    }
  }

  &__top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width:  100%;

    @media (max-width: 798px) {
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
    }
  }

  &__top-left {
    display: flex;
    align-items: center;
    gap: 24px;

    @media (max-width: 798px) {
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
    }
  }

  &__icon {
    margin-right: 16px;
  }

  &__top-text {
    display: flex;
    flex-direction: column;
    gap: 8px;

    p {
      margin-bottom: 0;
    }

    @media (max-width: 798px) {
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
    }
  }

  &__main-title {
    @include font-20px-title-modal;
  }

  &__description {
    @include font-15-text;
  }

  &__email {
    @include font-15-text;
  }

  &__info {
    @include font-properties(
      $font-family-inter,
      $font-size-15,
      $font-weight-400,
      normal,
      $gray-200
    );
  }

  &__top-right {
    display: flex;
    gap: 16px;
  }

  &__format {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    position: relative;

    &--form-select {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    @media (max-width: 798px) {
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
      gap: 10px;
    }
  }

  &__format-title {
    width: 100%;
    @include font-20px-title-modal;
  }

  &__item {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__item-left {
    display: flex;
    flex-direction: column;
  }

  &__item-left strong {
    @include font-16px-title;
  }

  &__item-left p {
    @include font-15-text;
    color: $gray-200;
    margin: 4px 0 0 0;
  }

  &__item-right {
    display: flex;
    gap: 16px;
  }

  &__badge {
    cursor: pointer;
  }

  &__footer {
    display: flex;
    gap: 16px;
    justify-content: flex-end;
    border-top: none !important;
    padding: 0;
  }

  &-input__error {
    @include input-error;
  }
}
</style>
